<template>
  <div class="py-4">
    <div class="mb-4">
      <DistributionListHeader />
    </div>
    <div class="bg-white text-left p-3">
      <div class="d-flex justify-content-between">
        <h2 class="text-h4 font-weight-semibold mb-4">
          {{ isEdit ? form.name : 'New List' }}
        </h2>
      </div>
      <b-row>
        <b-col class="col-md-6 col-sm-12">
          <b-form-group
            class="mb-3"
            label-size="sm"
            label-class="mb-0"
            label="Name *"
            label-for="name-input"
          >
            <b-form-input
              v-model="form.name"
              @blur="$v.form.name.$touch"
              :state="$v.form.name.$dirty ? !$v.form.name.$error : null"
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col class="col-md-6 col-sm-12 pt-3">
          <b-alert v-if="affectedUsersError" show variant="danger">
            <p class="text-body1 mb-0 py-1">
              <b-icon icon="exclamation-circle-fill"></b-icon>
              There was an error calculating the number of affected users
            </p>
          </b-alert>
          <b-alert
            v-else
            show
            :variant="
              !form.rules.length || !targetedUsersCount ? 'danger' : 'info'
            "
          >
            <p class="text-body1 mb-0 py-1">
              <b-icon icon="exclamation-circle-fill"></b-icon>
              This distribution list will target
              {{ form.rules.length ? targetedUsersCount : 0 }} users
              <b-icon
                :style="{
                  visibility: isCountingUsersAffected ? 'visible' : 'hidden',
                }"
                icon="arrow-clockwise"
                class="align-middle"
                animation="spin"
                font-scale="2"
              ></b-icon>
            </p>
          </b-alert>
        </b-col>
      </b-row>

      <b-row class="mb-3">
        <b-col class="col-md-9 col-sm-12">
          <DistributionListSelectRuleInput
            v-if="rulesLoaded"
            ref="ruleSelector"
            v-model="form.rules"
          />
        </b-col>
      </b-row>

      <div class="d-flex justify-content-between align-items-center">
        <div class="d-flex">
          <a
            v-if="canDelete"
            class="text-decoration-none cursor-pointer text-black text-caption font-weight-semibold"
            @click="onDelete"
          >
            <b-icon icon="trash" class="mr-1" />Delete distribution list
          </a>
          <b-alert
            :show="!canDelete && distributionListId"
            variant="warning"
            class="mb-0"
          >
            <p class="text-body1 mb-0 py-1">
              <b-icon icon="exclamation-triangle-fill"></b-icon>
              This list cannot be deleted because it is being used for a pending
              message
            </p>
          </b-alert>
        </div>
        <div class="d-flex align-items-center">
          <b-link
            class="text-decoration-none font-weight-semibold text-black mr-3"
            :to="{ name: 'MessageDistributionList' }"
          >
            Cancel
          </b-link>
          <b-button
            variant="warning"
            class="font-weight-semibold"
            :disabled="isFormValid"
            @click="onSave"
          >
            Save
          </b-button>
        </div>
      </div>
    </div>
    <ConfirmationModal
      :show="showConfirmDelete"
      @confirm="onDelete"
      @closed="closeConfirmationModel"
      message="Are you sure you want to delete this distribution list?"
    ></ConfirmationModal>
  </div>
</template>

<script>
import * as Sentry from '@sentry/vue';
import { mapState } from 'vuex';
import { required, maxLength } from 'vuelidate/lib/validators';
import BannerMixin from '@/mixins/BannerMixin.vue';
import DistributionListHeader from '@/components/super-admin/DistributionListHeader.vue';
import DistributionListSelectRuleInput from '@/components/super-admin/DistributionListSelectRuleInput.vue';
import ConfirmationModal from '@/components/super-admin/ConfirmModal.vue';
import { CRITERIA_TYPE, debounce, isDefined } from '@/utils';

export default {
  name: 'MessageDistributionListDetail',
  mixins: [BannerMixin],
  components: {
    DistributionListHeader,
    DistributionListSelectRuleInput,
    ConfirmationModal,
  },
  data() {
    return {
      rulesLoaded: false,
      canDelete: false,
      isCountingUsersAffected: false,
      showConfirmDelete: false,
      affectedUsersError: false,
      form: {
        name: '',
        rules: [
          {
            criteriaIdentifier: null,
            criteriaType: null,
            criteriaValues: '',
          },
        ],
      },
    };
  },
  watch: {
    'form.rules': {
      handler(v = []) {
        const rules = v.filter(
          (x) => x.criteriaIdentifier && x.criteriaType && x.criteriaValues
        );
        if (rules.length) {
          this.debouncedFetchAffectedUserCount(rules);
        }
      },
      deep: true,
    },
  },
  computed: {
    ...mapState('superAdmin', {
      targetedUsersCount: (s) => s.numberOfaffectedUsers,
      proxyOrganizations: (s) => s.proxyOrganizations,
    }),
    isEdit() {
      return !!this.$route.params.id;
    },
    isRemovable() {
      return !this.targetedUsersCount;
    },
    isFormValid() {
      return (
        this.$v.$invalid ||
        (this.$refs.ruleSelector && this.$refs.ruleSelector.$v.$invalid) ||
        !this.form.rules.length
      );
    },
    distributionListId() {
      return this.$route.params.id;
    },
  },
  validations: {
    form: {
      name: { required, maxLength: maxLength(256) },
    },
  },
  methods: {
    normalizeRules(rules) {
      return rules.reduce((acc, x) => {
        const criteria = Object.values(this.$pfm.CRITERIA_IDENTIFIER).find(
          (v) => v.label === x.criteriaIdentifier
        );

        if (!criteria) return acc;

        const criteriaType = x.criteriaType?.value;
        const isEmail =
          this.$pfm.CRITERIA_IDENTIFIER.EMAIL.label === x.criteriaIdentifier;
        const list = isEmail
          ? x.criteriaValues
              .split(',')
              .filter(isDefined)
              .filter((v) => v?.trim().length !== 0)
          : x.criteriaValues;
        try {
          let criteriaValues;
          // if (criteriaType === this.$pfm.CRITERIA_TYPE.INCLUDES) {
          if (Array.isArray(list)) {
            criteriaValues = list.map((c) => ({
              [criteria.field]: isEmail ? c : c.value,
            }));
          } else {
            criteriaValues = [{ [criteria.field]: list.value }];
          }
          return [...acc, { ...x, criteriaType, criteriaValues }];
        } catch (err) {
          Sentry.captureException(err);
          console.log('error while fetching normalizing rules: ', err);
        }
      }, []);
    },
    denormalizeRules(rules) {
      /*
      We need to transform our criteria from the API that looks like:

      [
       {
          criteriaIdentifier: 'UserOrganization',
          criteriaType: 'Includes',
          criteriaValues: [
          {
             organizationId: '6d7ddb2d-18b5-49d3-a092-faa4fbd3c52b'
          },
          {
             organizationId: '6d7ddb2d-2555-49d3-a092-faa4fbd25522'
          }
       }
      ]

      to the following format:
      [
        {
          criteriaIdentifier: 'UserOrganization',
          criteriaType: {
            label: 'includes',
            value: 'Includes',
          },
          criteriaValues: [{
            label: 'Performa Testing Group A',
            value: '6d7ddb2d-18b5-49d3-a092-faa4fbd3c52b'
          },
          {
            label: 'Performa Testing Group B',
            value: '6d7ddb2d-2555-49d3-a092-faa4fbd25522'
          }],
        }
      ]

      The main difficulty is that the label is not provided by the API for
      most of the criteria types which requires unique lookups from different
      data sources locally.
    */

      return rules.reduce((acc, r) => {
        const criteriaTypeObject = this.$pfm.CRITERIA_TYPE_OPTIONS.find(
          (cto) => {
            return cto.value === r.criteriaType;
          }
        );
        // Do a lookup on each constant hash table for the matching label/text
        let criteriaValueObjects;
        switch (r.criteriaIdentifier) {
          case this.$pfm.CRITERIA_IDENTIFIER.STATE.label: {
            criteriaValueObjects = this.transformDTOToViewModel(
              r.criteriaValues,
              'state',
              this.$pfm.STATES
            );
            break;
          }
          case this.$pfm.CRITERIA_IDENTIFIER.ORGANIZATION.label: {
            criteriaValueObjects = this.transformDTOToViewModel(
              r.criteriaValues,
              'organizationId',
              this.proxyOrganizations,
              'id',
              'name'
            );
            break;
          }
          case this.$pfm.CRITERIA_IDENTIFIER.ROLE.label: {
            criteriaValueObjects = this.transformDTOToViewModel(
              r.criteriaValues,
              'roleName',
              this.$pfm.ROLE_OPTIONS
            );
            break;
          }
          case this.$pfm.CRITERIA_IDENTIFIER.STATUS.label: {
            criteriaValueObjects = this.transformDTOToViewModel(
              r.criteriaValues,
              'courseAssignmentStatus',
              this.$pfm.STATUS_OPTION_LIST,
              'name'
            );
            break;
          }
          case this.$pfm.CRITERIA_IDENTIFIER.EMAIL.label: {
            criteriaValueObjects = r.criteriaValues
              .map((c) => c.email)
              .join(',');
            break;
          }
        }

        return [
          ...acc,
          {
            criteriaIdentifier: r.criteriaIdentifier,
            criteriaType: criteriaTypeObject,
            criteriaValues: criteriaValueObjects,
          },
        ];
      }, []);
    },
    transformDTOToViewModel(
      criteriaValues,
      criteriaField,
      options,
      valueField = 'value',
      labelField = 'text'
    ) {
      const criteriaValueObjects = [];
      for (let i = 0; i < criteriaValues.length; i++) {
        let criteriaValue = criteriaValues[i][criteriaField];
        let criteria = options.find((s) => {
          return s[valueField] === criteriaValue;
        });
        criteriaValueObjects.push({
          text: criteria[labelField],
          value: criteriaValue,
        });
      }
      return criteriaValueObjects.length === 1
        ? criteriaValueObjects[0]
        : criteriaValueObjects;
    },
    async onSave() {
      this.$v.$touch();
      this.$refs.ruleSelector.$v.$touch();
      if (this.$v.$invalid || this.$refs.ruleSelector.$v.$invalid) return;

      const payload = {
        ...this.form,
        rules: this.normalizeRules(this.form.rules),
      };

      if (this.distributionListId) {
        await this.updateDistributionList(payload);
      } else {
        await this.createDistributionList(payload);
      }
    },
    async createDistributionList(payload) {
      try {
        await this.$store.dispatch(
          'superAdmin/createMessageDistributionList',
          payload
        );

        this.showBanner(
          this.$bannerTypes.SUCCESS,
          '<span>Success</span> - The distribution list was created'
        );
        this.$router.push({
          name: 'MessageDistributionList',
        });
      } catch (err) {
        Sentry.captureException(err);
        const { data } = err.response || {};
        this.showBanner(
          this.$bannerTypes.ERROR,
          `<span>Failed</span> - ${
            data.errors?.[0]?.description ||
            'An error occurred when attempting to create the distribution list. Try again.'
          }`
        );
      }
    },
    async updateDistributionList(payload) {
      try {
        await this.$store.dispatch('superAdmin/updateMessageDistributionList', {
          id: this.distributionListId,
          list: payload,
        });

        this.showBanner(
          this.$bannerTypes.SUCCESS,
          '<span>Success</span> - The distribution list was created'
        );
        this.$router.push({
          name: 'MessageDistributionList',
        });
      } catch (err) {
        Sentry.captureException(err);
        const { data } = err.response || {};
        this.showBanner(
          this.$bannerTypes.ERROR,
          `<span>Failed</span> - ${
            data.errors?.[0]?.description ||
            'An error occurred when attempting to update the distribution list. Try again.'
          }`
        );
      }
    },
    async onDelete() {
      if (!this.showConfirmDelete) {
        this.showConfirmDelete = true;
        return;
      }
      try {
        await this.$store.dispatch(
          'superAdmin/deleteMessageDistributionLists',
          this.distributionListId
        );
        this.$router.push({
          name: 'MessageDistributionList',
        });
      } catch (err) {
        Sentry.captureException(err);
        const { data } = err.response || {};
        this.showBanner(
          this.$bannerTypes.ERROR,
          `<span>Failed</span> - ${
            data.errors?.[0]?.description ||
            'An error occurred when attempting to delete the distribution list. Try again.'
          }`
        );
      } finally {
        this.showConfirmDelete = false;
      }
    },
    async fetchDistributionListById() {
      try {
        const distributionList = await this.$store.dispatch(
          'superAdmin/getMessageDistributionListsById',
          this.distributionListId
        );
        // PERFCS-1997 - We removed "is", and "includes" became "is"
        // To avoid having to update data and the backend code we
        // do the translation here on the front end
        distributionList?.rules.forEach((rule) => {
          if (rule.criteriaType === CRITERIA_TYPE.IS) {
            rule.criteriaType = CRITERIA_TYPE.INCLUDES;
          }
        });

        const rules = this.denormalizeRules(distributionList.rules);
        this.canDelete = distributionList.canDelete;
        this.form = {
          name: distributionList.name,
          rules,
        };
        this.rulesLoaded = true;
      } catch (err) {
        Sentry.captureException(err);
        console.log('error while fetching distribution lists by id: ', err);
      }
    },
    debouncedFetchAffectedUserCount: debounce(function (rules) {
      this.fetchAffectedUsersCount(rules);
    }, 300),
    async fetchAffectedUsersCount(rules) {
      this.affectedUsersError = false;
      const payload = {
        name: this.form.name,
        rules: this.normalizeRules(rules),
      };
      this.isCountingUsersAffected = true;
      try {
        await this.$store.dispatch(
          'superAdmin/getNumberOfAffectedUsers',
          payload
        );
      } catch (err) {
        Sentry.captureException(err);
        console.log('error while fetching affected users: ', err);
        this.affectedUsersError = true;
      } finally {
        this.isCountingUsersAffected = false;
      }
    },
    closeConfirmationModel() {
      this.showConfirmDelete = false;
    },
  },
  created() {
    if (this.distributionListId) {
      this.fetchDistributionListById();
    } else {
      this.rulesLoaded = true;
    }
  },
};
</script>
